import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "../lib/api";
import { LEAD_STAGES, LEAD_SERVICE_OPTIONS, LEAD_PRODUCT_OPTIONS, LEAD_SOURCE_OPTIONS, LEAD_PROPOSAL_STATUS_OPTIONS, fmtINR } from "../lib/constants";
import { PROPOSAL_TYPES, PROPOSAL_TYPE_META } from "../lib/templates";
import {
  Plus, X, Search, Filter, Download, FileSpreadsheet, FileText, Eye, Edit3,
  Phone, Mail, MapPin, CalendarClock, ArrowUpDown, ChevronLeft, ChevronRight, CheckSquare,
  Square, MoreHorizontal, ArrowRight, ClipboardCheck, Sparkles, Upload, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

// Re-exported aliases for backwards compatibility within this file
const PROPOSAL_STATUS_OPTIONS = LEAD_PROPOSAL_STATUS_OPTIONS;
const SERVICE_OPTIONS = LEAD_SERVICE_OPTIONS;
const PRODUCT_OPTIONS = LEAD_PRODUCT_OPTIONS;
const SOURCE_OPTIONS = LEAD_SOURCE_OPTIONS;

const STAGE_COLOR = {
  Enquiry: "bg-slate-100 text-slate-700",
  Meeting: "bg-sky-100 text-sky-800",
  "Proposal Sent": "bg-amber-100 text-amber-800",
  Negotiation: "bg-orange-100 text-orange-800",
  "Work Order": "bg-emerald-100 text-emerald-800",
  Won: "bg-emerald-200 text-emerald-900",
  Lost: "bg-rose-100 text-rose-800",
};
const PROP_COLOR = {
  "Not Sent": "bg-slate-100 text-slate-700",
  Draft: "bg-slate-200 text-slate-800",
  Sent: "bg-sky-100 text-sky-800",
  Accepted: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-rose-100 text-rose-800",
};

const PAGE_SIZE = 10;

export default function Leads() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ stage: "", source: "", service: "", assigned: searchParams.get("assigned") || "", proposal: "" });
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  // Sync assigned filter when URL param changes
  useEffect(() => {
    const a = searchParams.get("assigned") || "";
    setFilters((f) => f.assigned === a ? f : { ...f, assigned: a });
  }, [searchParams]);

  // Modals
  const [openNew, setOpenNew] = useState(false);
  const [viewLead, setViewLead] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [followupLead, setFollowupLead] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    const [l, u] = await Promise.all([api.get("/leads"), api.get("/users")]);
    setLeads(l.data);
    setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  // Filter + search + sort
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return leads
      .filter((l) => {
        if (filters.stage && l.stage !== filters.stage) return false;
        if (filters.source && l.source !== filters.source) return false;
        if (filters.service && (l.service_required || l.project_type || "") !== filters.service) return false;
        if (filters.assigned && l.assigned_executive !== filters.assigned) return false;
        if (filters.proposal && (l.proposal_status || "Not Sent") !== filters.proposal) return false;
        if (!s) return true;
        const hay = [l.lead_number, l.company_name, l.project_name, l.project_type, l.location,
                     l.contact_person, l.phone, l.email, l.source, l.stage].join(" ").toLowerCase();
        return hay.includes(s);
      })
      .sort((a, b) => {
        const k = sort.key;
        const av = (a[k] || "").toString().toLowerCase();
        const bv = (b[k] || "").toString().toLowerCase();
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
  }, [leads, search, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelAll = () => {
    if (selected.size === pageItems.length) setSelected(new Set());
    else setSelected(new Set(pageItems.map((i) => i.id)));
  };

  const bulkAssign = async (uid) => {
    if (selected.size === 0) return toast.error("No leads selected");
    await Promise.all([...selected].map((id) => api.put(`/leads/${id}`, { assigned_executive: uid })));
    toast.success(`${selected.size} leads assigned`);
    setSelected(new Set());
    load();
  };
  const bulkStage = async (stage) => {
    if (selected.size === 0) return toast.error("No leads selected");
    await Promise.all([...selected].map((id) => api.put(`/leads/${id}`, { stage })));
    toast.success(`${selected.size} leads → ${stage}`);
    setSelected(new Set());
    load();
  };

  const updateLead = async (id, patch) => {
    await api.put(`/leads/${id}`, patch);
    load();
  };

  const headers = [
    { k: "lead_number", label: "Lead ID" },
    { k: "company_name", label: "Client" },
    { k: "project_name", label: "Project", hideOnMobile: true },
    { k: "location", label: "Location", hideOnMobile: true },
    { k: "service_required", label: "Service", hideOnMobile: true },
    { k: "contact_person", label: "Contact", hideOnMobile: true },
    { k: "phone", label: "Mobile", hideOnMobile: true },
    { k: "source", label: "Source", hideOnMobile: true },
    { k: "assigned_executive", label: "Executive", hideOnMobile: true },
    { k: "proposal_status", label: "Proposal" },
    { k: "stage", label: "Status" },
    { k: "next_followup_date", label: "Next FU", hideOnMobile: true },
  ];

  return (
    <div className="space-y-5 animate-fade-up" style={{ fontFamily: "Poppins, sans-serif" }}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#72BE44] font-semibold mb-2">Sales Pipeline</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F2A4A]">Leads</h1>
          <p className="text-stone-500 mt-1 text-sm">{leads.length} leads · {filtered.length} matching filters</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`${API}/leads/export.csv`} target="_blank" rel="noreferrer" data-testid="export-csv-btn"
             className="flex items-center gap-2 px-3 py-2 bg-white border border-stone-300 hover:border-[#72BE44] rounded-lg text-xs font-medium text-[#0F2A4A] transition">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </a>
          <a href={`${API}/leads/export.pdf`} target="_blank" rel="noreferrer" data-testid="export-pdf-btn"
             className="flex items-center gap-2 px-3 py-2 bg-white border border-stone-300 hover:border-[#72BE44] rounded-lg text-xs font-medium text-[#0F2A4A] transition">
            <FileText className="w-3.5 h-3.5" /> PDF
          </a>
          <button onClick={() => setImportOpen(true)} data-testid="import-leads-btn"
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-stone-300 hover:border-[#72BE44] rounded-lg text-xs font-medium text-[#0F2A4A] transition">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Import Sheet
          </button>
          <button onClick={() => setOpenNew(true)} data-testid="new-lead-btn"
                  className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium" style={{ background: "#0F2A4A" }}>
            <Plus className="w-4 h-4" /> New Lead
          </button>
        </div>
      </div>

      {/* Search + Filters bar */}
      <div className="bg-white border border-stone-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search"
            placeholder="Search by client, project, contact, phone..."
            className="w-full pl-10 pr-3 py-2 bg-stone-50 border border-transparent rounded-lg focus:bg-white focus:border-[#72BE44] focus:outline-none text-sm transition"
          />
        </div>
        <Sel value={filters.stage} onChange={(v) => setFilters({ ...filters, stage: v })} placeholder="All Statuses" options={LEAD_STAGES} testid="filter-stage" />
        <Sel value={filters.proposal} onChange={(v) => setFilters({ ...filters, proposal: v })} placeholder="All Proposal" options={PROPOSAL_STATUS_OPTIONS} testid="filter-proposal" />
        <Sel value={filters.service} onChange={(v) => setFilters({ ...filters, service: v })} placeholder="All Services" options={SERVICE_OPTIONS} testid="filter-service" />
        <Sel value={filters.source} onChange={(v) => setFilters({ ...filters, source: v })} placeholder="All Sources" options={SOURCE_OPTIONS} testid="filter-source" />
        <Sel value={filters.assigned} onChange={(v) => setFilters({ ...filters, assigned: v })} placeholder="All Executives"
             options={users.filter((u) => u.role !== "client").map((u) => ({ value: u.id, label: u.name }))} testid="filter-assigned" />
        {(filters.stage || filters.proposal || filters.service || filters.source || filters.assigned || search) && (
          <button onClick={() => { setFilters({ stage: "", source: "", service: "", assigned: "", proposal: "" }); setSearch(""); }}
                  className="text-xs text-stone-500 hover:text-rose-600 px-2 py-1">Clear all</button>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selected.size > 0 && (
        <div className="bg-[#0F2A4A] text-white rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="h-4 w-px bg-white/30" />
          <select onChange={(e) => e.target.value && bulkAssign(e.target.value)} data-testid="bulk-assign"
                  defaultValue="" className="bg-white/10 border border-white/20 text-white text-xs px-2 py-1.5 rounded">
            <option value="" disabled>Assign to...</option>
            {users.filter((u) => u.role !== "client").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select onChange={(e) => e.target.value && bulkStage(e.target.value)} data-testid="bulk-stage"
                  defaultValue="" className="bg-white/10 border border-white/20 text-white text-xs px-2 py-1.5 rounded">
            <option value="" disabled>Move to stage...</option>
            {LEAD_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => setSelected(new Set())} className="text-xs text-white/70 hover:text-white ml-auto">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider sticky top-0">
              <tr>
                <th className="px-3 py-3 w-10">
                  <button onClick={toggleSelAll} data-testid="select-all">
                    {selected.size === pageItems.length && pageItems.length > 0
                      ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  </button>
                </th>
                {headers.map((h) => (
                  <th key={h.k} className={`px-3 py-3 text-left font-semibold cursor-pointer hover:bg-white/10 ${h.hideOnMobile ? "hidden md:table-cell" : ""}`}
                      onClick={() => toggleSort(h.k)} data-testid={`sort-${h.k}`}>
                    <span className="inline-flex items-center gap-1">{h.label}
                      {sort.key === h.k && <ArrowUpDown className="w-3 h-3" />}</span>
                  </th>
                ))}
                <th className="px-3 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`}
                    className={`border-t border-stone-100 hover:bg-[#72BE44]/5 cursor-pointer ${selected.has(l.id) ? "bg-emerald-50/40" : ""}`}>
                  <td className="px-3 py-2.5">
                    <button onClick={(e) => { e.stopPropagation(); toggleSel(l.id); }} data-testid={`sel-${l.id}`}>
                      {selected.has(l.id) ? <CheckSquare className="w-4 h-4 text-[#72BE44]" /> : <Square className="w-4 h-4 text-stone-400" />}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-stone-700" onClick={() => setViewLead(l)}>
                    {l.lead_number || (l.id || "").slice(0, 8)}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-[#0F2A4A]" onClick={() => setViewLead(l)}>{l.company_name}</td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-stone-700" onClick={() => setViewLead(l)}>{l.project_name || l.project_type || "—"}</td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-xs text-stone-600" onClick={() => setViewLead(l)}>{l.location || "—"}</td>
                  <td className="hidden md:table-cell px-3 py-2.5" onClick={() => setViewLead(l)}>
                    {(l.service_required || l.project_type) && (
                      <span className="text-[10px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">{l.service_required || l.project_type}</span>
                    )}
                  </td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-xs" onClick={() => setViewLead(l)}>{l.contact_person}</td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-xs font-mono" onClick={() => setViewLead(l)}>{l.phone || "—"}</td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-xs text-stone-600" onClick={() => setViewLead(l)}>{l.source}</td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-xs" onClick={() => setViewLead(l)}>{userName(l.assigned_executive)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${PROP_COLOR[l.proposal_status || "Not Sent"]}`}>
                      {l.proposal_status || "Not Sent"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${STAGE_COLOR[l.stage] || "bg-stone-100 text-stone-700"}`}>
                      {l.stage}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-xs text-stone-600">
                    {l.next_followup_date ? (
                      <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3 text-amber-600" />{l.next_followup_date}</span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <IconBtn title="View" onClick={() => setViewLead(l)} testid={`act-view-${l.id}`}><Eye className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Edit" onClick={() => setEditLead(l)} testid={`act-edit-${l.id}`}><Edit3 className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Follow-up" onClick={() => setFollowupLead(l)} testid={`act-fu-${l.id}`}><CalendarClock className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Convert to Proposal" onClick={() => setConvertLead({ ...l, mode: "proposal" })} testid={`act-prop-${l.id}`}><FileText className="w-3.5 h-3.5" /></IconBtn>
                      <IconBtn title="Convert to Project" onClick={() => setConvertLead({ ...l, mode: "project" })} testid={`act-conv-${l.id}`}><ClipboardCheck className="w-3.5 h-3.5" /></IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
              {pageItems.length === 0 && (
                <tr><td colSpan={14} className="text-center py-16 text-stone-400">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />No leads match your filters
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 text-xs text-stone-600">
          <div>Showing <b>{Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}</b> – <b>{Math.min(page * PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b></div>
          <div className="flex items-center gap-1">
            <button disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="page-prev"
                    className="p-1.5 rounded hover:bg-stone-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-3 py-1 bg-stone-100 rounded font-medium">{page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"
                    className="p-1.5 rounded hover:bg-stone-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {openNew && <LeadFormModal mode="new" users={users} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />}
      {editLead && <LeadFormModal mode="edit" lead={editLead} users={users} onClose={() => setEditLead(null)} onSaved={() => { setEditLead(null); load(); }} />}
      {viewLead && <LeadDrawer lead={viewLead} users={users} onClose={() => setViewLead(null)} onUpdate={(p) => { updateLead(viewLead.id, p); setViewLead({ ...viewLead, ...p }); }} />}
      {followupLead && <FollowupModal lead={followupLead} onClose={() => setFollowupLead(null)} onSaved={() => { setFollowupLead(null); load(); }} />}
      {convertLead && <ConvertModal lead={convertLead} mode={convertLead.mode} onClose={() => setConvertLead(null)} onDone={() => { setConvertLead(null); load(); }} />}
      {importOpen && <ImportLeadsModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
    </div>
  );
}

// =============== IMPORT LEADS FROM SHEET (CSV / Excel) ===============
function ImportLeadsModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return toast.error("Choose a CSV or Excel file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.created > 0) toast.success(`${data.created} leads imported successfully`);
      else toast.error("No new leads were imported — see details below");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal title="Import Leads from Sheet" onClose={result ? onDone : onClose}>
      <div className="space-y-4">
        <div className="bg-[#72BE44]/10 border border-[#72BE44]/30 rounded-lg p-3 text-xs text-[#0F2A4A]">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5 text-[#72BE44]" /> Supported: CSV, Excel (.xlsx)</div>
          <p>First row must be a header. Recognised columns: <b>Company Name*</b>, Contact Person, Email, Phone, Source, Project Name, Service Required, Location, Estimated Value, Notes, Next Followup Date. Duplicate leads (same email or company+phone) are skipped automatically.</p>
          <a href={`${API}/leads/import-template.csv`} target="_blank" rel="noreferrer" data-testid="download-template-link"
             className="inline-flex items-center gap-1 mt-2 text-[#0F2A4A] font-semibold underline hover:text-[#72BE44]">
            <Download className="w-3 h-3" /> Download sample template
          </a>
        </div>

        {!result && (
          <>
            <label
              data-testid="import-dropzone"
              className="block border-2 border-dashed border-stone-300 hover:border-[#72BE44] rounded-xl p-8 text-center cursor-pointer transition"
            >
              <Upload className="w-8 h-8 mx-auto text-stone-400 mb-2" />
              {file ? (
                <div className="text-sm font-medium text-[#0F2A4A]">{file.name} <span className="text-stone-400">({Math.round(file.size / 1024)} KB)</span></div>
              ) : (
                <div className="text-sm text-stone-500">Click to choose your lead sheet (.csv / .xlsx)</div>
              )}
              <input type="file" hidden accept=".csv,.xlsx,.xlsm,.xls" data-testid="import-file-input"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <button onClick={upload} disabled={!file || uploading} data-testid="import-submit"
                    className="w-full text-white py-2.5 rounded-lg font-medium disabled:opacity-40 flex items-center justify-center gap-2"
                    style={{ background: "#0F2A4A" }}>
              <Upload className="w-4 h-4" /> {uploading ? "Importing..." : "Import Leads"}
            </button>
          </>
        )}

        {result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-700 mb-1" />
                <div className="text-2xl font-bold text-emerald-800" data-testid="import-created-count">{result.created}</div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Leads Created</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <AlertTriangle className="w-5 h-5 mx-auto text-amber-700 mb-1" />
                <div className="text-2xl font-bold text-amber-800">{result.skipped.length}</div>
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Rows Skipped</div>
              </div>
            </div>
            {result.skipped.length > 0 && (
              <div className="max-h-44 overflow-y-auto border border-stone-200 rounded-lg divide-y divide-stone-100">
                {result.skipped.map((s, i) => (
                  <div key={i} className="px-3 py-2 text-xs text-stone-600 flex gap-2">
                    <span className="font-mono text-stone-400">Row {s.row}</span>
                    <span>{s.reason}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={onDone} data-testid="import-done"
                    className="w-full bg-[#72BE44] hover:bg-[#5da636] text-white py-2.5 rounded-lg font-medium">
              Done — View Leads
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function IconBtn({ children, title, onClick, testid }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title={title} data-testid={testid}
            className="p-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A] transition">{children}</button>
  );
}

function Sel({ value, onChange, placeholder, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="text-xs px-2.5 py-2 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-[#72BE44]">
      <option value="">{placeholder}</option>
      {options.map((o) => typeof o === "string"
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-stone-200 sticky top-0 bg-white">
          <h2 className="font-bold text-lg text-[#0F2A4A]">{title}</h2>
          <button onClick={onClose} data-testid="modal-close" className="p-1 rounded hover:bg-stone-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, options }) {
  if (options) {
    return (
      <div>
        <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required}
                className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm">
          {options.map((o) => typeof o === "string" ? <option key={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required}
             className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm" />
    </div>
  );
}

function LeadFormModal({ mode, lead, users, onClose, onSaved }) {
  const [form, setForm] = useState(lead ? { ...lead } : {
    company_name: "", contact_person: "", email: "", phone: "", source: "Website",
    project_name: "", project_type: "", service_required: "Environment Clearance + Integrated CTE",
    service_required_other: "", product_required: "NA", product_required_other: "",
    location: "", estimated_value: 0, stage: "Enquiry", proposal_status: "Not Sent",
    assigned_executive: "", notes: "", next_followup_date: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0 };
      // Clear "_other" fields if their parent is not "Other - Specify"
      if (payload.service_required !== "Other - Specify") payload.service_required_other = "";
      if (payload.product_required !== "Other - Specify") payload.product_required_other = "";
      if (mode === "new") await api.post("/leads", payload);
      else await api.put(`/leads/${lead.id}`, payload);
      toast.success(mode === "new" ? "Lead created" : "Lead updated");
      onSaved();
    } catch { toast.error("Failed"); }
  };
  return (
    <Modal title={mode === "new" ? "New Lead" : `Edit ${lead.lead_number || ""}`} onClose={onClose} wide>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Company / Client Name" value={form.company_name} onChange={(v) => set("company_name", v)} required testid="lead-company" />
        <Field label="Project Name" value={form.project_name} onChange={(v) => set("project_name", v)} testid="lead-project-name" />
        <Field label="Contact Person" value={form.contact_person} onChange={(v) => set("contact_person", v)} required testid="lead-contact" />
        <Field label="Mobile" value={form.phone} onChange={(v) => set("phone", v)} testid="lead-phone" />
        <Field label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testid="lead-email" />
        <Field label="Location of Project" value={form.location} onChange={(v) => set("location", v)} testid="lead-location" />

        <Field label="Service Required" value={form.service_required} onChange={(v) => set("service_required", v)} options={SERVICE_OPTIONS} testid="lead-service" />
        {form.service_required === "Other - Specify" ? (
          <Field label="Service — Please Specify" value={form.service_required_other} onChange={(v) => set("service_required_other", v)} testid="lead-service-other" required />
        ) : <div />}

        <Field label="Product Required" value={form.product_required} onChange={(v) => set("product_required", v)} options={PRODUCT_OPTIONS} testid="lead-product" />
        {form.product_required === "Other - Specify" ? (
          <Field label="Product — Please Specify" value={form.product_required_other} onChange={(v) => set("product_required_other", v)} testid="lead-product-other" required />
        ) : <div />}

        <Field label="Lead Source" value={form.source} onChange={(v) => set("source", v)} options={SOURCE_OPTIONS} testid="lead-source" />
        <Field label="Assigned Executive" value={form.assigned_executive} onChange={(v) => set("assigned_executive", v)}
               options={[{ value: "", label: "— Unassigned —" }, ...users.filter((u) => u.role !== "client").map((u) => ({ value: u.id, label: u.name }))]} testid="lead-assigned" />
        <Field label="Estimated Value (₹)" type="number" value={form.estimated_value} onChange={(v) => set("estimated_value", v)} testid="lead-value" />
        <Field label="Lead Status" value={form.stage} onChange={(v) => set("stage", v)} options={LEAD_STAGES} testid="lead-stage" />
        <Field label="Proposal Status" value={form.proposal_status} onChange={(v) => set("proposal_status", v)} options={PROPOSAL_STATUS_OPTIONS} testid="lead-prop-status" />
        <Field label="Next Follow-up Date" type="date" value={form.next_followup_date} onChange={(v) => set("next_followup_date", v)} testid="lead-next-fu" />
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Notes</label>
          <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="lead-notes"
                    className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm" />
        </div>
        <button type="submit" data-testid="lead-submit"
                className="md:col-span-2 w-full text-white py-2.5 rounded-lg font-medium" style={{ background: "#72BE44" }}>
          {mode === "new" ? "Create Lead" : "Save Changes"}
        </button>
      </form>
    </Modal>
  );
}

function LeadDrawer({ lead, users, onClose, onUpdate }) {
  const [followups, setFollowups] = useState([]);
  useEffect(() => { api.get(`/leads/${lead.id}/followups`).then((r) => setFollowups(r.data)).catch(() => {}); }, [lead.id]);
  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl animate-slide-in" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
        <div className="sticky top-0 bg-[#0F2A4A] text-white p-5 z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#72BE44] font-bold">{lead.lead_number || "Lead"}</div>
              <h2 className="text-xl font-bold mt-1">{lead.company_name}</h2>
              <div className="text-xs text-slate-300 mt-1">{lead.project_name || lead.project_type || "—"}</div>
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${STAGE_COLOR[lead.stage]}`}>{lead.stage}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${PROP_COLOR[lead.proposal_status || "Not Sent"]}`}>{lead.proposal_status || "Not Sent"}</span>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <Row k="Service Required" v={lead.service_required === "Other - Specify" ? `Other — ${lead.service_required_other || "?"}` : (lead.service_required || lead.project_type)} />
          <Row k="Product Required" v={lead.product_required === "Other - Specify" ? `Other — ${lead.product_required_other || "?"}` : (lead.product_required || "—")} />
          <Row k="Contact Person" v={lead.contact_person} />
          <Row k="Mobile" v={lead.phone && <a href={`tel:${lead.phone}`} className="text-[#0F2A4A] font-medium">{lead.phone}</a>} />
          <Row k="Email" v={lead.email && <a href={`mailto:${lead.email}`} className="text-[#0F2A4A] font-medium">{lead.email}</a>} />
          <Row k="Location" v={lead.location} />
          <Row k="Lead Source" v={lead.source} />
          <Row k="Assigned Executive" v={userName(lead.assigned_executive)} />
          <Row k="Estimated Value" v={fmtINR(lead.estimated_value)} />
          <Row k="Last Follow-up" v={lead.last_followup_date} />
          <Row k="Next Follow-up" v={lead.next_followup_date} />
          {lead.notes && <Row k="Notes" v={lead.notes} />}

          <div className="pt-4 border-t border-stone-200">
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Follow-up History ({followups.length})</div>
            {followups.length === 0 ? <div className="text-xs text-stone-400">No follow-ups yet.</div> :
              <div className="space-y-2">
                {followups.map((f) => (
                  <div key={f.id} className="border-l-2 border-[#72BE44] pl-3 pb-2">
                    <div className="text-xs text-stone-500">{(f.at || "").slice(0, 16).replace("T", " ")} · {f.by_user_name}</div>
                    <div className="text-sm text-stone-800">{f.note}</div>
                    {f.outcome && <div className="text-xs text-[#72BE44] mt-0.5"><b>Outcome:</b> {f.outcome}</div>}
                  </div>
                ))}
              </div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-sm border-b border-stone-100 pb-2">
      <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{k}</div>
      <div className="text-stone-800 text-right">{v || "—"}</div>
    </div>
  );
}

function FollowupModal({ lead, onClose, onSaved }) {
  const [form, setForm] = useState({ note: "", outcome: "", next_date: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/leads/${lead.id}/followup`, form);
      toast.success("Follow-up logged");
      onSaved();
    } catch { toast.error("Failed"); }
  };
  return (
    <Modal title={`Log Follow-up — ${lead.company_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Discussion Notes</label>
          <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={4} required
                    data-testid="fu-note"
                    className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm" />
        </div>
        <Field label="Outcome" value={form.outcome} onChange={(v) => setForm({ ...form, outcome: v })} testid="fu-outcome"
               options={["", "Interested", "Pricing requested", "Hold", "Not interested", "Send proposal"]} />
        <Field label="Next Follow-up Date" type="date" value={form.next_date} onChange={(v) => setForm({ ...form, next_date: v })} testid="fu-next-date" />
        <button type="submit" data-testid="fu-submit"
                className="w-full text-white py-2.5 rounded-lg font-medium" style={{ background: "#72BE44" }}>
          Save Follow-up
        </button>
      </form>
    </Modal>
  );
}

function ConvertModal({ lead, mode, onClose, onDone }) {
  const [serviceType, setServiceType] = useState(lead.service_required === "EC" ? "Fresh EC" : (lead.service_required === "CRZ" ? "CRZ Clearance" : "Fresh EC"));
  const inferProposalType = () => {
    const s = (lead.service_required || lead.project_type || "").toLowerCase();
    if (s.includes("crz") && s.includes("ec")) return "CRZ";  // CRZ + EC mapping → use CRZ template (covers EC + CRZ)
    if (s.includes("crz")) return "CRZ";
    if (s.includes("consent to operate")) return "CTO";
    if (s.includes("consent to establish")) return "Fresh_EC_CTE";
    if (s.includes("amendment")) return "EC_Amendment";
    if (s.includes("expansion")) return "EC_Expansion";
    if (s.includes("corrigendum")) return "EC_Corrigendum";
    if (s.includes("revalidation")) return "EC_Amendment";  // revalidation maps to amendment template
    if (s.includes("mep")) return "MEP";
    if (s.includes("ecmp") || s.includes("post monitoring") || s.includes("form v") || s.includes("post ec")) return "Compliance_Monitoring";
    if (s.includes("environment clearance") || s.includes("integrated cte")) return "Fresh_EC_CTE";
    return "Fresh_EC_CTE";
  };
  const [proposalType, setProposalType] = useState(inferProposalType());

  const convertToProposal = async () => {
    try {
      const { data } = await api.post(`/proposals/from-lead/${lead.id}`, { proposal_type: proposalType });
      toast.success(`Proposal ${data.proposal_number} created`);
      onDone();
    } catch { toast.error("Failed"); }
  };

  const convertToProject = async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert-to-project`, {
        service_type: serviceType,
        service_category: serviceType.includes("Clearance") || serviceType.includes("EC") ? "Environmental Clearance"
                          : (serviceType.includes("CT") ? "MPCB" : "Environmental Systems"),
      });
      const cred = data._new_client_credentials;
      if (cred) {
        toast.success(
          <div className="flex flex-col gap-1">
            <div className="font-semibold">Project created + client login provisioned</div>
            <div className="text-xs"><b>Email:</b> {cred.email}</div>
            <div className="text-xs"><b>Password:</b> {cred.initial_password} (force-reset on first login)</div>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.success(`Project created — charter, timeline, folders, requirements & KYC auto-loaded`);
      }
      onDone();
    } catch { toast.error("Failed"); }
  };

  return (
    <Modal title={mode === "proposal" ? `Convert to Proposal — ${lead.company_name}` : `Convert to Project — ${lead.company_name}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-[#72BE44]/10 border border-[#72BE44]/30 rounded-lg p-3 text-xs text-[#0F2A4A] flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#72BE44]" />
          <span>{mode === "proposal" ? "Creates a Draft proposal from the matching PT Ventures template — pre-fills cover page, intro, applicability, scope, fees, T&Cs."
                                    : "Creates a new Project from this lead — auto-loads the charter, timeline, document checklist, 14 folders, requirements and KYC (pre-filled from lead details). A client portal login is auto-created from the lead's email + phone."}</span>
        </div>
        {mode === "proposal" ? (
          <>
            <Field label="Proposal Template" value={proposalType} onChange={setProposalType}
                   options={PROPOSAL_TYPES.map((t) => ({ value: t, label: PROPOSAL_TYPE_META[t].label }))} testid="conv-prop-type" />
            <button onClick={convertToProposal} data-testid="conv-prop-submit"
                    className="w-full text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2" style={{ background: "#0F2A4A" }}>
              <ArrowRight className="w-4 h-4" /> Create Proposal
            </button>
          </>
        ) : (
          <>
            <Field label="Service Type" value={serviceType} onChange={setServiceType}
                   options={["Fresh EC", "Amendment EC", "Expansion EC", "Corrigendum EC", "CRZ Clearance", "CTE", "CTO", "ECMP Retainer", "STP", "ETP", "AQMS"]} testid="conv-svc" />
            <button onClick={convertToProject} data-testid="conv-proj-submit"
                    className="w-full text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2" style={{ background: "#0F2A4A" }}>
              <ArrowRight className="w-4 h-4" /> Create Project
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

// Re-export for back-compat with other pages that imported Modal/Input/Select from Leads.jsx
export { Modal };
export function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid}
             className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm" />
    </div>
  );
}
export function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid}
                className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm" />
    </div>
  );
}
export function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm">
        {options.map((o) => typeof o === "string" ? <option key={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
